import { z } from 'zod'
import { clockDurationMinutes } from './time'

export const clockTime = z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/, 'Enter a time like 08:30.')

const timeRangeMessage = { message: 'Start time must be earlier than end time.', path: ['endTime'] }

export const createTimeEntryInput = z
  .object({
    personId: z.string().min(1),
    projectId: z.string().min(1),
    taskId: z.string().min(1).nullable().optional(),
    workDate: z.iso.date(),
    startTime: clockTime,
    endTime: clockTime,
    description: z.string().trim().min(1).max(500).nullable().optional(),
    billable: z.boolean()
  })
  .refine(
    (input) => clockDurationMinutes(input.startTime, input.endTime) !== null,
    timeRangeMessage
  )

export type CreateTimeEntryInput = z.infer<typeof createTimeEntryInput>

export const listTimeEntriesInput = z
  .object({
    personId: z.string().min(1).optional(),
    from: z.iso.date().optional(),
    to: z.iso.date().optional()
  })
  .refine((input) => !input.from || !input.to || input.from <= input.to, {
    message: 'The start date must be on or before the end date.'
  })

export type ListTimeEntriesInput = z.infer<typeof listTimeEntriesInput>

export const timeEntryId = z.string().min(1)
export const updateTimeEntryInput = z
  .object({
    personId: z.string().min(1).optional(),
    projectId: z.string().min(1).optional(),
    taskId: z.string().min(1).nullable().optional(),
    workDate: z.iso.date().optional(),
    startTime: clockTime.optional(),
    endTime: clockTime.optional(),
    description: z.string().trim().min(1).max(500).nullable().optional(),
    billable: z.boolean().optional()
  })
  .refine((input) => Object.keys(input).length > 0, {
    message: 'Provide at least one time entry field to update.'
  })
export type UpdateTimeEntryInput = z.infer<typeof updateTimeEntryInput>

export const timeEntry = z
  .object({
    id: z.string().min(1),
    personId: z.string().min(1),
    projectId: z.string().min(1),
    taskId: z.string().min(1).nullable(),
    workDate: z.iso.date(),
    durationMinutes: z.number().int().positive(),
    startTime: clockTime,
    endTime: clockTime,
    description: z.string().min(1).max(500).nullable(),
    billable: z.boolean(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime()
  })
  .refine(
    (entry) => clockDurationMinutes(entry.startTime, entry.endTime) === entry.durationMinutes,
    { message: 'The duration must match the start and end time.', path: ['durationMinutes'] }
  )

export type TimeEntry = z.infer<typeof timeEntry>
